import { read, save } from '@/utils/async-storage';
import { logger } from '@/utils/logging';
import { sleep } from '@/utils/promises';

import { BetrAnalytics } from '.';
import { activateRemoteConfig, refetchRemoteConfigs } from './hooks/use-firebase-remote-config';

const LOG_TAG = '[Segmentation]';

/**
 * Bump this version when adding keys for RemoteConfig values that are aimed to be
 * A/B tested based on this segmentation mechanism.
 *
 * This will ensure those configs are re-fetched and activated, after we determine the segmentation.
 */
const AB_TEST_VERSION = 1;
const AB_TEST_VERSION_KEY = 'ab_test_version_key';

/**
 * Utility responsible for splitting the user in 5 equally weighted groups, based on their numeric Global ID.
 * Sets a firebase `user_id_bucket` on each user, that can be `bucket_usersids_[01|23|45|67|89]`.
 * This will allow us to direct RemoteConfigs to just a specific sub-group of users, and also be able to deterministically
 * identify in other tools (such as BigQuery) what each user received. (otherwise, this is impossible with Firebase A/B testing.)
 */
class UserSegmentation {
    async setUserBucketBasedOnUserId(userGlobalId: string) {
        const group = this.getUserGroupFromUserId(userGlobalId);
        if (group) {
            try {
                await BetrAnalytics.setUserCustomSignal('user_id_bucket', group);
                logger.debug(LOG_TAG, 'User group set:', group);
                await this.refetchConfigsIfNeeded();
            } catch (e) {
                logger.warn(LOG_TAG, 'fail setting up segmentation', e);
            }
        } else {
            logger.warn(LOG_TAG, 'Could not determine user bucket for userGlobalId', userGlobalId);
        }
    }

    async refetchConfigsIfNeeded() {
        const storedVersion = Number((await read(AB_TEST_VERSION_KEY)) || 0);
        const refetch = AB_TEST_VERSION > storedVersion;
        if (refetch) {
            logger.debug('A/B tests updated, refetch remote configs...');
            await save(AB_TEST_VERSION_KEY, AB_TEST_VERSION + '');
            //Delay 5 seconds, so re-fetches are not colliding with potential in-flight fetches,
            //as this causes errors in RNFirebas, and requests get cancled and end up in an error state
            await sleep(5000);
            await refetchRemoteConfigs();
            await activateRemoteConfig();
            logger.debug(LOG_TAG, 'remote configs re-fetched & activated.');
        }
    }

    getUserGroupFromUserId(userGlobalId: string) {
        let group;
        switch (true) {
            case /[01]$/.test(userGlobalId):
                group = 'bucket_usersids_01';
                break;
            case /[23]$/.test(userGlobalId):
                group = 'bucket_usersids_23';
                break;
            case /[45]$/.test(userGlobalId):
                group = 'bucket_usersids_45';
                break;
            case /[67]$/.test(userGlobalId):
                group = 'bucket_usersids_67';
                break;
            case /[89]$/.test(userGlobalId):
                group = 'bucket_usersids_89';
                break;
        }
        return group;
    }
}

export const Segmentation = new UserSegmentation();
